import type { ParentFanoutRecord } from './types'

export type BoardAckDelayStat = {
  boardId: string
  boardTitle: string | null
  averageDelayMs: number
  sampleCount: number
}

export type FanoutHealthSummary = {
  loadedParentCount: number
  evaluableBoardSlots: number
  appearedCount: number
  ackedCount: number
  contentUnchangedCount: number
  unexplainedMissingCount: number
  unknownCount: number
  appearedRate: number | null
  ackedRate: number | null
  contentUnchangedRate: number | null
  unexplainedMissingRate: number | null
  p50AckDelayMs: number | null
  p95AckDelayMs: number | null
  ackDelaySampleCount: number
}

export type BoardScorecardRow = {
  boardId: string
  boardTitle: string | null
  parentSamples: number
  appearedCount: number
  ackedCount: number
  contentUnchangedCount: number
  unexplainedMissingCount: number
  appearedRate: number | null
  ackedRate: number | null
  contentUnchangedRate: number | null
  averageDelayMs: number | null
  medianDelayMs: number | null
  ackDelaySampleCount: number
  flags: {
    slowAck: boolean
    lowAppearRate: boolean
    lowAckRate: boolean
  }
}

const MIN_SAMPLES_FOR_RATE = 3
const SLOW_ACK_MS = 20_000
const LOW_APPEAR_RATE = 0.5
const LOW_ACK_RATE = 0.8

const rate = (numerator: number, denominator: number) =>
  denominator === 0 ? null : numerator / denominator

const percentile = (values: number[], p: number) => {
  if (values.length === 0) return null
  const sorted = [...values].sort((a, b) => a - b)
  const index = Math.min(sorted.length - 1, Math.max(0, Math.ceil(p * sorted.length) - 1))
  return sorted[index]
}

const median = (values: number[]) => {
  if (values.length === 0) return null
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2
  }
  return sorted[mid]
}

const isRowEvaluable = (record: ParentFanoutRecord, row: ParentFanoutRecord['rows'][number]) =>
  record.matchStatus === 'ready' && row.coverageStatus === 'complete'

export const formatDelayMs = (delayMs: number) => {
  if (delayMs < 1000) {
    return `${Math.round(delayMs)} ms`
  }
  const seconds = delayMs / 1000
  return seconds < 10 ? `${seconds.toFixed(1)} s` : `${Math.round(seconds)} s`
}

export const formatRate = (value: number | null) => (value == null ? '—' : `${Math.round(value * 100)}%`)

type BoardAccumulator = {
  boardTitle: string | null
  parentSamples: number
  appearOpportunitySamples: number
  appearedCount: number
  appearedInOpportunityCount: number
  ackedCount: number
  contentUnchangedCount: number
  unexplainedMissingCount: number
  delays: number[]
}

const getOldestParentId = (records: ParentFanoutRecord[]) => {
  if (records.length === 0) return null
  return records.reduce((oldest, record) =>
    record.parent.created < oldest.parent.created ? record : oldest
  ).parent.id
}

const countsTowardAppearRate = (
  record: ParentFanoutRecord,
  row: ParentFanoutRecord['rows'][number],
  oldestParentId: string | null
) => {
  if (!isRowEvaluable(record, row)) return false
  if (oldestParentId != null && record.parent.id === oldestParentId) return false
  if (!row.matchedAppearance && row.noAppearanceReason === 'no_content_changed') return false
  return true
}

const collectBoardAccumulators = (records: ParentFanoutRecord[]) => {
  const byBoard = new Map<string, BoardAccumulator>()
  const allAckDelays: number[] = []
  const oldestParentId = getOldestParentId(records)
  let evaluableBoardSlots = 0
  let appearOpportunitySlots = 0
  let appearedCount = 0
  let appearedInOpportunityCount = 0
  let ackedCount = 0
  let contentUnchangedCount = 0
  let unexplainedMissingCount = 0
  let unknownCount = 0

  for (const record of records) {
    for (const row of record.rows) {
      if (!isRowEvaluable(record, row)) {
        unknownCount += 1
        continue
      }

      evaluableBoardSlots += 1
      const countsForAppearRate = countsTowardAppearRate(record, row, oldestParentId)
      const boardStats =
        byBoard.get(row.board.id) ??
        ({
          boardTitle: row.board.title,
          parentSamples: 0,
          appearOpportunitySamples: 0,
          appearedCount: 0,
          appearedInOpportunityCount: 0,
          ackedCount: 0,
          contentUnchangedCount: 0,
          unexplainedMissingCount: 0,
          delays: []
        } satisfies BoardAccumulator)

      boardStats.parentSamples += 1
      if (countsForAppearRate) {
        appearOpportunitySlots += 1
        boardStats.appearOpportunitySamples += 1
      }

      if (row.matchedAppearance) {
        appearedCount += 1
        boardStats.appearedCount += 1
        if (countsForAppearRate) {
          appearedInOpportunityCount += 1
          boardStats.appearedInOpportunityCount += 1
        }

        const ackTimestamp = record.acksByAppearanceId.get(row.matchedAppearance.id)
        if (record.ackStatus === 'ready' && ackTimestamp != null) {
          ackedCount += 1
          boardStats.ackedCount += 1
          const delayMs = Math.max(0, Math.round(ackTimestamp - record.parent.created))
          allAckDelays.push(delayMs)
          boardStats.delays.push(delayMs)
        }
      } else if (row.noAppearanceReason === 'no_content_changed') {
        contentUnchangedCount += 1
        boardStats.contentUnchangedCount += 1
      } else if (countsForAppearRate) {
        unexplainedMissingCount += 1
        boardStats.unexplainedMissingCount += 1
      }

      byBoard.set(row.board.id, boardStats)
    }
  }

  return {
    byBoard,
    allAckDelays,
    evaluableBoardSlots,
    appearOpportunitySlots,
    appearedCount,
    appearedInOpportunityCount,
    ackedCount,
    contentUnchangedCount,
    unexplainedMissingCount,
    unknownCount
  }
}

export const computeFanoutHealthSummary = (records: ParentFanoutRecord[]): FanoutHealthSummary => {
  const {
    allAckDelays,
    evaluableBoardSlots,
    appearOpportunitySlots,
    appearedCount,
    appearedInOpportunityCount,
    ackedCount,
    contentUnchangedCount,
    unexplainedMissingCount,
    unknownCount
  } = collectBoardAccumulators(records)

  return {
    loadedParentCount: records.length,
    evaluableBoardSlots,
    appearedCount,
    ackedCount,
    contentUnchangedCount,
    unexplainedMissingCount,
    unknownCount,
    appearedRate: rate(appearedInOpportunityCount, appearOpportunitySlots),
    ackedRate: rate(ackedCount, appearedCount),
    contentUnchangedRate: rate(contentUnchangedCount, evaluableBoardSlots),
    unexplainedMissingRate: rate(unexplainedMissingCount, appearOpportunitySlots),
    p50AckDelayMs: percentile(allAckDelays, 0.5),
    p95AckDelayMs: percentile(allAckDelays, 0.95),
    ackDelaySampleCount: allAckDelays.length
  }
}

export const computeBoardScorecard = (records: ParentFanoutRecord[]): BoardScorecardRow[] => {
  const { byBoard } = collectBoardAccumulators(records)

  return Array.from(byBoard.entries())
    .map(([boardId, stats]) => {
      const appearedRate = rate(stats.appearedInOpportunityCount, stats.appearOpportunitySamples)
      const ackedRate = rate(stats.ackedCount, stats.appearedCount)
      const averageDelayMs =
        stats.delays.length === 0
          ? null
          : stats.delays.reduce((sum, delay) => sum + delay, 0) / stats.delays.length

      return {
        boardId,
        boardTitle: stats.boardTitle,
        parentSamples: stats.parentSamples,
        appearedCount: stats.appearedCount,
        ackedCount: stats.ackedCount,
        contentUnchangedCount: stats.contentUnchangedCount,
        unexplainedMissingCount: stats.unexplainedMissingCount,
        appearedRate,
        ackedRate,
        contentUnchangedRate: rate(stats.contentUnchangedCount, stats.parentSamples),
        averageDelayMs,
        medianDelayMs: median(stats.delays),
        ackDelaySampleCount: stats.delays.length,
        flags: {
          slowAck: stats.delays.length >= 2 && averageDelayMs != null && averageDelayMs >= SLOW_ACK_MS,
          lowAppearRate:
            stats.appearOpportunitySamples >= MIN_SAMPLES_FOR_RATE &&
            appearedRate != null &&
            appearedRate < LOW_APPEAR_RATE,
          lowAckRate:
            stats.appearedCount >= MIN_SAMPLES_FOR_RATE && ackedRate != null && ackedRate < LOW_ACK_RATE
        }
      }
    })
    .sort((left, right) => (right.averageDelayMs ?? -1) - (left.averageDelayMs ?? -1))
}

export const computeBoardAckDelayStats = (
  records: ParentFanoutRecord[],
  topN = 5
): BoardAckDelayStat[] =>
  computeBoardScorecard(records)
    .filter(row => row.averageDelayMs != null)
    .slice(0, topN)
    .map(row => ({
      boardId: row.boardId,
      boardTitle: row.boardTitle,
      averageDelayMs: row.averageDelayMs!,
      sampleCount: row.ackDelaySampleCount
    }))
